import type { SubItemCategory } from '../types/subItemCategory';
import type { CarpetPricingCatalogItem } from '../types/carpetPricingCatalog';
import type { EquipmentType, EquipmentServiceRate } from '../types/equipmentCatalog';
import type { SubItemTypeMetaMap } from '../types/subItemTypeMeta';

export interface EquipmentCatalogForTypeMeta {
  types: Pick<EquipmentType, 'key' | 'label'>[];
  serviceRates: Pick<EquipmentServiceRate, 'key' | 'label'>[];
}

export function buildSubItemTypeMetaMap(
  categories: SubItemCategory[],
  carpetCatalog: CarpetPricingCatalogItem[],
  equipmentCatalog?: EquipmentCatalogForTypeMeta,
): SubItemTypeMetaMap {
  const map: SubItemTypeMetaMap = {};

  // Carpet first so that sub_item_categories wins on collision.
  for (const item of carpetCatalog) {
    map[item.key] = {
      label: item.label,
      pricingMode: item.pricingMode ?? 'qty_rate',
      unitLabel: item.unitLabel ?? null,
    };
  }

  // Equipment catalog. Equipment_* keys never live in sub_item_categories — this is
  // their only label source, parallel to carpet. Markup/tax flags default to false in
  // breakdownCalculator when a key is absent from `categories`, which matches the
  // intended charge-through behavior.
  if (equipmentCatalog) {
    for (const t of equipmentCatalog.types) {
      map[`equipment_unit_${t.key}`] = { label: t.label, pricingMode: 'amount', unitLabel: null };
    }
    for (const r of equipmentCatalog.serviceRates) {
      map[`equipment_service_${r.key}`] = { label: r.label, pricingMode: 'amount', unitLabel: null };
    }
    map.equipment_bill_discount = {
      label: 'Equipment bill discount',
      pricingMode: 'amount',
      unitLabel: null,
    };
    // Per-item discount rows. The item being discounted is named in the note.
    map.equipment_item_discount = {
      label: 'Discount',
      pricingMode: 'amount',
      unitLabel: null,
    };
  }

  for (const cat of categories) {
    map[cat.key] = {
      label: cat.label,
      pricingMode: cat.pricingMode,
      unitLabel: null,
    };
  }

  return map;
}
